import { calculateLotLevelPL, } from "@bracket/pnl";
import type { LoadedVault, LoadedVaultWithPnl, } from "@types";
import { effectZero, usdToAsset, } from "@utils";
import { parseUnits, } from "viem";

export const applyVaultModifiers = async (vault: LoadedVault): Promise<LoadedVaultWithPnl> => {

	const vaultWithPnl = await applyPnlToVault(vault);

	const { exposure: { current_value, }, meta: { apy, }, denomination, symbol: __symbol, } = vault;
	const est_annual_earnings_usd = current_value.usd * (apy / 100);
	vaultWithPnl.exposure.est_annual_earnings = {
		usd: est_annual_earnings_usd,
		brkt: await usdToAsset(est_annual_earnings_usd, denomination),
	};

	return vaultWithPnl;
}


export const applyPnlToVault = async (actual: LoadedVault): Promise<LoadedVaultWithPnl> => {

	const vault = Object.assign({}, actual);
	const { transactions, denomination: { decimals, }, } = vault;
	const closed = vault.symbol === "BBOR1";

	try {

		const { search, } = globalThis.location;
		if(search.toLowerCase().includes("testnav=")) {
			const vals = search.slice(1).split("&").reduce((acc, str) => {
				const [k, v] = str.split("=");
				acc[k.toLowerCase()] = v;
				return acc;
			}, {} as { [k: string]: string; });
			vault.meta.share_price = Number(vals.testnav);
			vault.nav.nav = parseUnits(vals.testnav, decimals).toString();
		}

		const { transactions: pnl, closeout, } = await calculateLotLevelPL({
			transactions,
			decimals,
			currentSharePrice: vault.meta.share_price,
			normalize: true,
			nav: vault.nav.nav,
		});

		vault.transactions = pnl;

		const current_value_usd = closeout ? closeout.currentValueUSD : effectZero(pnl.reduce((acc, t) => t.trxn_type === "DEPOSIT" ? acc + t.remainder * vault.meta.share_price : acc, 0));
		const unrealized_gain_usd = effectZero(pnl.reduce((acc, { trxn_type, unrealizedGain, }) => trxn_type === "DEPOSIT" ? acc + unrealizedGain : acc, 0));
		const realized_gain_usd = effectZero(pnl.reduce((acc, { trxn_type, realizedGain, }) => ["TRANSFER", "WITHDRAW"].includes(trxn_type) ? acc + realizedGain : acc, 0));

		let open_cost_basis = effectZero(pnl.reduce((acc, t) => t.trxn_type === "DEPOSIT" ? acc + t.basis_open : acc, 0));
		open_cost_basis = closeout ? open_cost_basis -closeout.openDepositAmount : open_cost_basis;

		const open_cost_basis_brkt = effectZero(pnl.reduce((acc, t) => t.trxn_type === "DEPOSIT" ? acc + BigInt(Math.floor((t.basis_open / t.price_eth) * t.brkteth_conv * (10**decimals))) : acc, 0n));

		const orig_cost_basis_stored = effectZero(pnl.reduce((acc, t) => t.trxn_type === "DEPOSIT" ? acc + t.basis : acc, 0));
		const orig_cost_basis = closeout ? orig_cost_basis_stored - closeout.openDepositAmount : orig_cost_basis_stored;

		const orig_cost_basis_brkt = effectZero(pnl.reduce((acc, t) => t.trxn_type === "DEPOSIT" ? acc + BigInt(Math.floor((t.basis / t.price_eth) * t.brkteth_conv * (10**decimals))) : acc, 0n));

		const pnl_usd = effectZero(realized_gain_usd + unrealized_gain_usd);
		const apy_usd = effectZero(pnl_usd / orig_cost_basis);
		const exposure = Object.assign({}, vault.exposure);

		exposure.current_value = {
			usd: current_value_usd,
			brkt: await usdToAsset(current_value_usd, vault.denomination),
		};

		exposure.unrealized_gain = {
			usd: unrealized_gain_usd,
			brkt: await usdToAsset(unrealized_gain_usd, vault.denomination),
			perc: (unrealized_gain_usd / open_cost_basis) * 100 || 0,
		};

		exposure.realized_gain = {
			usd: realized_gain_usd,
			brkt: await usdToAsset(realized_gain_usd, vault.denomination),
		}

		exposure.cost_basis = {
			usd: open_cost_basis,
			brkt: open_cost_basis_brkt.toString(),
		};

		exposure.orig_cost_basis = {
			usd: orig_cost_basis,
			brkt: orig_cost_basis_brkt.toString(),
		};

		exposure.pnl = {
			usd: realized_gain_usd + unrealized_gain_usd,
			brkt: await usdToAsset(realized_gain_usd + unrealized_gain_usd, vault.denomination),
			perc: effectZero((realized_gain_usd  + unrealized_gain_usd) / orig_cost_basis_stored) * 100 || 0,
		};

		exposure.shares = closed ? 0 :pnl.reduce((acc, t) => {
			switch(t.trxn_type) {
				case "DEPOSIT": {	
					return acc + effectZero(t.shares_num);
				}
				case "WITHDRAW": {
					return acc - effectZero(t.shares_num);
				}
				default: {
					return acc;
				}
			}
		}, 0);

		exposure.apy = {
			usd: apy_usd,
			brkt: await usdToAsset(apy_usd, vault.denomination),
			perc: effectZero(apy_usd) * 100 || 0,
		}

		vault.exposure = exposure;

		return vault as LoadedVaultWithPnl;

	}
	catch(err) {

		throw new Error(`applyPnlToVault failed for vault ${vault.symbol} - ${err instanceof Error ? err.message : "without error instance"}`);
	}
}
